import {useCallback, useContext, useEffect, useState} from "react";
import {ToastContext} from "Toast";
import {useMessages} from "i18n";
import {useIsLoggedIn, useUser} from "UserProvider";
import {AccountBrandApi} from "account/AccountBrandApi";
import {AccountThemeApi} from "account/AccountThemeApi";
import {admitPickedFiles} from "core/images/pickedImageBatch";
import {compressPickedImage} from "core/images/compressPickedImage";
import {ImageCompressionError} from "core/images/imageCompression";
import {BACKDROP_IMAGE_TARGET, LOGO_IMAGE_TARGET} from "core/images/uploadImageTargets";
import {PAGE_THEME_TEMPLATES, type SponsorOptions} from "sponsor/sponsorBooking";
import {
	validateSponsorOptions, withSponsorOption,
	type PageThemeTemplateKey, type SponsorOptionErrors, type SponsorOptionKey,
} from "sponsor/sponsorOptions";
import {useHalloweenSlidesOn} from "home/landing/useHalloweenCampaign";
import {PersonalizationApi} from "./PersonalizationApi";
import {subscribeComposerTheme} from "./composerThemeRequest";
import {
	addPersonalizationImage,
	clearPersonalizationTemplate,
	emptyPersonalizationDraft,
	NO_INHERITED_PERSONALIZATION,
	personalizationBlocksSend,
	personalizationDraftError,
	personalizationDraftRung,
	personalizationFieldOrigin,
	personalizationNote,
	personalizationOptionsForWire,
	preselectPersonalizationTemplate,
	removePersonalizationImage,
	toInheritedPersonalization,
	toPersonalizationRequest,
	type PersonalizationDraftError,
	type PersonalizationDraftRequest,
	type PersonalizationDraftState,
	type PersonalizationField,
	type PersonalizationFieldOrigin,
	type PersonalizationInherited,
	type PersonalizationRung,
} from "./personalizationDraft";
import {
	offeredFonts, PERSONALIZATION_FONT_ORDER, type PersonalizationFontKey,
} from "./personalizationFont";
import {
	offeredTemplates,
	personalizationAccess,
	personalizationAvailability,
	personalizationOfferHolder,
	composerOnHolderChange,
	templateImageArity,
	templateTakesOptions,
	type PersonalizationAccess,
	type PersonalizationAvailability,
	type PersonalizationOffer,
	type PersonalizationTemplateArity,
} from "./personalizationOffer";
import {personalizationTemplateGroups, type PersonalizationTemplateGroup} from "./personalizationTemplateGroups";

/**
 * React adapter for the per-transfer personalization composer (ui#556).
 *
 * A THIN adapter, per the epic #26 mandate: it holds the draft, calls the services and hands the domain
 * every decision. Every rule it looks like it makes (is this sendable, which fields are inherited) is a
 * call into `personalizationDraft.ts` / `personalizationOffer.ts`, which are tested
 * on their own and survive this hook being rewritten.
 */
export interface PersonalizationVM {
	/** The server's offer, or `null` until it answers (and for a signed-out sender, who has no rung). */
	offer: PersonalizationOffer | null;
	availability: PersonalizationAvailability;
	/**
	 * What this visitor may DO with the composer (ui#764) — the view's gate, not `availability`.
	 * `locked` renders the whole surface inert, with the guest line and a way to sign in.
	 */
	access: PersonalizationAccess;
	loading: boolean;

	draft: PersonalizationDraftState;
	/** What this transfer would look like unpersonalized — the "inherited" side of every label. */
	inherited: PersonalizationInherited;
	/** The treatments this brand offers, in the picker's order. */
	templates: readonly PageThemeTemplateKey[];
	/**
	 * The picker's labelled groups over {@link templates}: the scenes only while the Halloween campaign's slides
	 * are on (owner decision 2026-10-27, kitecorp/transferify-ui#3776), `personalizationTemplateGroups`' rule.
	 */
	templateGroups: readonly PersonalizationTemplateGroup[];
	/**
	 * Whether the chosen look takes the poster settings (kitecorp/transferify-api-service#1149).
	 *
	 * Read from the OFFER's category, so the panel appears for exactly the looks the api calls
	 * wallpapers — including none at all, on an api that predates the widening. That is what lets this
	 * builder ship before the api half deploys: nothing is offered that the live api would refuse.
	 */
	takesOptions: boolean;
	/** Per-control messages for the option panel, keyed by option — the sponsor form's own verdict. */
	optionErrors: SponsorOptionErrors;
	/** The display faces this brand offers, in the picker's order — the api's list, never a local one. */
	fonts: readonly PersonalizationFontKey[];
	/** The chosen template's image arity, as the api publishes it. */
	arity: PersonalizationTemplateArity | null;
	/**
	 * What the draft asks for: nothing, free copy, or branding (ui#1814). The completion payload and the
	 * success screen both read it, so "which fields are branding" is decided once.
	 */
	rung: PersonalizationRung;

	setEnabled: (enabled: boolean) => void;
	/**
	 * Drops everything composed and starts again — what a NEW transfer needs.
	 *
	 * Explicit since ui#1814 lifted this hook out of `TransferPanel`. The panel unmounted for the whole
	 * progress and success phase, so every send used to reset the draft by destroying it; the builder now
	 * has to outlive that transition, and without this the second transfer would silently go out wearing
	 * the first one's page.
	 */
	reset: () => void;
	/** `null` clears the treatment, which is what a text-only personalization has. */
	setTemplate: (template: PageThemeTemplateKey | null) => void;
	/**
	 * A look handed in from OUTSIDE the builder (kitecorp/transferify-ui#3776): the Halloween slide's scene, put on
	 * the draft and armed at once, so the next send carries it without the builder opening.
	 * `preselectPersonalizationTemplate` is the rule.
	 */
	preselectTemplate: (template: PageThemeTemplateKey) => void;
	/** The off switch beside the opener: the look goes, and the draft stays armed only while something else remains. */
	clearTemplate: () => void;
	/** Sets one wallpaper setting, or clears it with `undefined`. */
	setOption: <K extends SponsorOptionKey>(key: K, value: SponsorOptions[K] | undefined) => void;
	/** `null` restores the app's standard face. */
	setFont: (font: PersonalizationFontKey | null) => void;
	setDisplayName: (value: string) => void;
	/** The note's optional heading — folded into the wire `message`, never sent as its own field. */
	setTitle: (value: string) => void;
	setMessage: (value: string) => void;
	setAccentColor: (value: string) => void;
	clearAccentColor: () => void;
	uploadLogo: (logo: File) => Promise<void>;
	removeLogo: () => void;
	/**
	 * Admits a whole pick in pick order (ui#779, sharing ui#664's rule): the leading files up to the
	 * template's remaining capacity upload sequentially, the rest are counted in {@link overflow}.
	 */
	uploadImages: (images: readonly File[]) => Promise<void>;
	removeImage: (assetId: string) => void;
	/** True while an asset upload is in flight; the sheet disables its pickers on it. */
	uploading: boolean;
	/** How many of the last pick were refused for want of a slot; cleared when a removal frees one. */
	overflow: number;

	/** Why this draft cannot be sent, or `null`. */
	error: PersonalizationDraftError | null;
	/** True when the draft is armed but not yet sendable — the panel's submit waits on this. */
	blocksSend: boolean;
	/** What the creation request should carry, or `null` for an unpersonalized transfer. */
	request: PersonalizationDraftRequest | null;
	/**
	 * The sender's note as the TRANSFER carries it — the same folded string the personalization gets.
	 *
	 * Sent on both because two different readers need it: the branded download page renders
	 * `PersonalizationDraft.message`, and the delivery email renders the transfer's own. One piece of
	 * state, two carriers, and no second place to edit it.
	 *
	 * EMPTY for a draft the sender did not commit. Dismissing the builder means "send the ordinary page",
	 * and a note that survived that would reach the recipient's inbox from a builder the sender had just
	 * cancelled — the one path on which the two carriers could disagree.
	 */
	note: string;
	originOf: (field: PersonalizationField) => PersonalizationFieldOrigin;
}

/**
 * @param active whether the composer is open. The offer and the account's own personalization are
 *   fetched on the FIRST open rather than on mount: three GETs on every landing-page view, for a sheet
 *   most senders never open, is a cost the create panel should not pay — and a signed-out sender's
 *   offer call would only 401.
 * @param takesThemeRequests whether this composer is the one a slide press or a `?tema=` arrival hands its
 *   scene to (`composerThemeRequest`, kitecorp/transferify-ui#3776): the landing's create panel, never the
 *   success card's, which composes for a transfer already sent.
 */
export function usePersonalizationDraft(active: boolean, takesThemeRequests = false): PersonalizationVM {
	const loggedIn = useIsLoggedIn();
	const [user] = useUser();
	const holder = personalizationOfferHolder(loggedIn, user);
	const messages = useMessages();
	const {showErrorToast} = useContext(ToastContext);

	const [offer, setOffer] = useState<PersonalizationOffer | null>(null);
	const [inherited, setInherited] = useState<PersonalizationInherited>(NO_INHERITED_PERSONALIZATION);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [overflow, setOverflow] = useState(0);
	// Starts with NO backdrop since ui#1814: seeding the first catalogue template would put every draft
	// on the BRANDING rung before the sender chose anything at all.
	const [draft, setDraft] = useState<PersonalizationDraftState>(emptyPersonalizationDraft);
	// Drops everything composed: a new transfer's `reset`, and an account change below.
	const discardDraft = () => {
		setDraft(emptyPersonalizationDraft());
		setOverflow(0);
	};

	// Whom the offer on hand was read for, and the last account signed in here (`personalizationOfferHolder`).
	// What a change forgets is `composerOnHolderChange`'s answer, applied in this render, so nothing of the
	// previous account is painted and the read below asks again. Set while rendering, React's pattern for state
	// that follows a value.
	const [heldFor, setHeldFor] = useState(holder);
	const [lastAccount, setLastAccount] = useState(holder);
	const holderChange = composerOnHolderChange({heldFor, holder, lastAccount});
	if (holderChange.forgetOffer) {
		setHeldFor(holder);
		setLastAccount(holderChange.lastAccount);
		setOffer(null);
		setInherited(NO_INHERITED_PERSONALIZATION);
		setLoading(false);
		if (holderChange.discardDraft) {
			discardDraft();
		}
	}

	// Read once per account: the offer is the catalogue, and it no longer depends on the plan
	// (kitecorp/transferify#525), so an upgrade in place has nothing to re-read. A failed read leaves it null,
	// and it is asked again when the composer is next activated, for a new transfer, or for another account.
	useEffect(() => {
		if (!active || holder === null || offer !== null) {
			return;
		}
		let disposed = false;
		setLoading(true);
		void (async () => {
			try {
				// The offer decides whether anything is composable at all, so it is the only one whose
				// failure matters. The account's own brand and theme are the INHERITED side of the
				// labels; a sender who has neither is the common case, so their reads settle
				// independently and a failure degrades to "inherits nothing" rather than to a dead sheet.
				const [offered, brand, theme] = await Promise.all([
					PersonalizationApi.getOffer(),
					AccountBrandApi.getBrand().catch(() => null),
					AccountThemeApi.getTheme().catch(() => null),
				]);
				if (disposed) {
					return;
				}
				setOffer(offered);
				setInherited(toInheritedPersonalization(brand, theme));
			} catch {
				// Silent: the offer stays null, which the sheet shows as still loading, until the composer is
				// next activated. A toast here would fire on every open while the api is unreachable.
			} finally {
				if (!disposed) {
					setLoading(false);
				}
			}
		})();
		return () => {
			disposed = true;
		};
	}, [active, holder, offer]);

	useEffect(() => {
		if (!takesThemeRequests) return;
		return subscribeComposerTheme(template => setDraft(current => preselectPersonalizationTemplate(current, template)));
	}, [takesThemeRequests]);

	const access = personalizationAccess(loggedIn);
	// A locked preview shows the CATALOGUE, which the api only publishes to an authenticated caller. It
	// deliberately advertises every treatment this bundle can DRAW, ahead of any offer: it is a shop
	// window for a visitor who cannot compose or send anything from it (`disabled={inert}` on every
	// control), so showing less than the app can do would undersell it. That is the one place in this
	// hook that does not go through the offer, and the only cost is a signed-out visitor seeing a look a
	// brand might not sell them once they sign in. The per-template ARITY deliberately stays absent
	// rather than borrowing the sponsor asset range, which counts what a template may CHOOSE FROM and not
	// what it composes: a wrong "uses between 3 and 6 images" would be worse than no hint at all.
	const templates = access === "locked"
		? PAGE_THEME_TEMPLATES
		: offeredTemplates(offer, PAGE_THEME_TEMPLATES);
	// The campaign's switch, read on the composer's first activation through the page's one memoised read (the
	// landing's own read when the composer is on it); the constant days decide until it answers, as on the landing.
	const templateGroups = personalizationTemplateGroups(templates, useHalloweenSlidesOn(active));
	// The api's own published list, filtered to the faces this build can paint and ordered by the picker's
	// own order. A locked visitor gets none: `offer` is null for them, and a font they cannot choose is
	// not worth advertising as a row of specimens they cannot press.
	const fonts = offeredFonts(offer, PERSONALIZATION_FONT_ORDER);
	const arity = templateImageArity(offer, draft.template);
	const takesOptions = templateTakesOptions(offer, draft.template);
	// The sponsor form's own verdict, on the sponsor form's own grammar, over the object that would
	// actually be SENT — the same input `personalizationDraftError` judges. Judged on the raw editing
	// state instead, the two disagree about a key the chosen look does not take: this would mark a
	// control in error while the send stayed unblocked.
	const optionErrors: SponsorOptionErrors = draft.template === null
		? {}
		: validateSponsorOptions(draft.template, personalizationOptionsForWire(draft) ?? {});
	const error = personalizationDraftError(draft, offer);
	const request = toPersonalizationRequest(draft, offer);

	const upload = useCallback(async (
		file: File,
		target: typeof LOGO_IMAGE_TARGET,
		send: (compressed: File) => Promise<{assetId: string; url: string}>,
		apply: (asset: {assetId: string; url: string}) => void,
	) => {
		setUploading(true);
		try {
			apply(await send(await compressPickedImage(file, target)));
		} catch (error) {
			showErrorToast(error instanceof ImageCompressionError
				? messages.sender.personalise.errors.imageUnprocessable
				: messages.sender.personalise.errors.uploadFailed);
		} finally {
			setUploading(false);
		}
	}, [messages.sender.personalise.errors, showErrorToast]);

	return {
		offer,
		availability: personalizationAvailability(offer),
		access,
		loading,
		draft,
		inherited,
		templates,
		templateGroups,
		takesOptions,
		optionErrors,
		fonts,
		arity,
		rung: personalizationDraftRung(draft),
		setEnabled: enabled => setDraft(current => ({...current, enabled})),
		reset: discardDraft,
		setTemplate: template => setDraft(current => ({
			...current,
			template,
			// Clearing the backdrop clears its imagery with it: images with nothing to render them into
			// are refused by the api as contradictory, and leaving them behind would strand the sender on
			// an `imagesWithoutTemplate` error they did not cause and cannot see the cause of.
			images: template === null ? [] : current.images,
		})),
		preselectTemplate: template => setDraft(current => preselectPersonalizationTemplate(current, template)),
		clearTemplate: () => setDraft(clearPersonalizationTemplate),
		// The shared one-key update, so all three editing surfaces clear a setting the same way.
		setOption: (key, value) => setDraft(current => ({
			...current,
			options: withSponsorOption(current.options, key, value),
		})),
		setFont: font => setDraft(current => ({...current, font})),
		setDisplayName: displayName => setDraft(current => ({...current, displayName})),
		setTitle: title => setDraft(current => ({...current, title})),
		setMessage: message => setDraft(current => ({...current, message})),
		setAccentColor: accentColor => setDraft(current => ({...current, accentColor})),
		clearAccentColor: () => setDraft(current => ({...current, accentColor: null})),
		uploadLogo: logo => upload(logo, LOGO_IMAGE_TARGET, PersonalizationApi.uploadLogo,
			asset => setDraft(current => ({...current, logo: asset}))),
		removeLogo: () => setDraft(current => ({...current, logo: null})),
		uploadImages: async images => {
			// The template's ceiling bounds the admission; a missing arity (offer still loading) admits
			// nothing rather than uploading images the draft could not carry.
			const capacity = arity?.maximumImages ?? 0;
			const admission = admitPickedFiles(images, draft.images.length, capacity);
			setOverflow(admission.refused);
			for (const image of admission.admitted) {
				// Sequential on purpose (the ui#664 rule): pick order IS the animation order, and a
				// parallel fan-out would let a slow compression land a later pick first.
				await upload(image, BACKDROP_IMAGE_TARGET, PersonalizationApi.uploadImage,
					asset => setDraft(current => ({...current, images: addPersonalizationImage(current.images, asset)})));
			}
		},
		removeImage: assetId => {
			setDraft(current => ({...current, images: removePersonalizationImage(current.images, assetId)}));
			// A freed slot retires the stale notice — the same rule the personalize page settled on
			// (delete clears; merely unusing must not).
			setOverflow(0);
		},
		uploading,
		overflow,
		error,
		blocksSend: personalizationBlocksSend(access, draft.enabled, error),
		request,
		note: draft.enabled ? personalizationNote(draft) : "",
		originOf: field => personalizationFieldOrigin(field, draft, inherited),
	};
}
